import {
  EscPosEncoder,
  bytesToBase64,
  connectionLabel,
  kotDocument,
  kotPrinters,
  profileForPaper,
  receiptDocument,
  receiptPrinter,
  renderEscPos,
  testPageDocument,
  tokenPrinter,
  tokenSlipDocument,
} from '@shripos/shared/escpos';
import { KotView, ReceiptView } from '../../components/ReceiptView.jsx';
import { ThermalPreview } from '../../components/ThermalPreview.jsx';
import { useSession } from '../../stores/session.js';
import { api, queryClient } from '../api.js';
import { cachedGet } from '../offline/cache.js';
import { fetchCatalog } from '../catalog.js';
import { withOverride } from '../override.js';
import { printNode } from '../print.js';
import { DevicePrinterError, sendToDevicePrinter } from './devices.js';
import { localAgentStatus, printViaLocalAgent } from './local.js';
import { logoBitmap, qrMatrix, rasterizeText } from './raster.js';

/**
 * PrinterService (spec §6): print(job) / status() / testPrint() over every transport.
 *   agent   → this PC's agent on 127.0.0.1 when enabled, else the server relay
 *   device  → WebUSB / Web Serial / Web Bluetooth in this browser
 *   none    → browser print dialog (never opened automatically)
 * Documents come from the shared templates, so paper, preview and e-receipt match.
 */
export class PrintError extends Error {
  constructor(message, { pending = false, reconnect = false, printer = null, fallback = null } = {}) {
    super(message);
    this.pending = pending; // queued for an offline agent; will print later
    this.reconnect = reconnect;
    this.printer = printer;
    this.fallback = fallback; // () => browser print of the same document
  }
}

const outletId = () => useSession.getState().me?.outlet?.id ?? null;
const myDeviceId = () => context?.deviceId ?? useSession.getState().device?.device?.id ?? null;

export function printersQueryKey() {
  return ['printers', outletId()];
}

/**
 * Self-order kiosks have no staff session: they hand their printers and catalog over here.
 */
let context = null;
export function setPrintContext(ctx) {
  context = ctx;
}

export async function loadPrinters() {
  if (context) return context.printers;
  if (!outletId()) return [];
  return queryClient.fetchQuery({
    queryKey: printersQueryKey(),
    // Kept on the device: printing to a local agent or USB printer works offline too.
    queryFn: () => cachedGet('/printers', `printers:${outletId()}`).then((r) => r.items),
    staleTime: 30_000,
    networkMode: 'always',
  });
}

async function catalog() {
  if (context) return context.catalog;
  return queryClient.fetchQuery({
    queryKey: ['catalog'],
    queryFn: fetchCatalog,
    staleTime: 5 * 60_000,
    networkMode: 'always',
  });
}

/** Document → bytes for one printer (logo, regional text and QR rasters done in the browser). */
export async function renderFor(printer, doc, { openDrawer = false, logoUrl = null } = {}) {
  const profile = printer.profile;
  const images = {};
  if (logoUrl && profile.printLogo && doc.blocks.some((b) => b.t === 'image' && b.name === 'logo'))
    images.logo = await logoBitmap(logoUrl, profile).catch(() => null);
  return renderEscPos(doc, profile, { images, rasterizeText, qrMatrix, openDrawer });
}

/** Send bytes over the printer's transport. Resolves when printed; throws PrintError otherwise. */
export async function sendBytes(printer, bytes, { kind, ref } = {}) {
  const c = printer.connection;
  if (c.via === 'device') {
    if (c.deviceId !== myDeviceId())
      throw new PrintError(
        `${printer.name} is connected to ${printer.deviceName ?? 'another device'}. Print from that device.`,
        { printer },
      );
    try {
      await sendToDevicePrinter(printer, bytes);
      return { via: 'device' };
    } catch (err) {
      throw new PrintError(`${printer.name}: ${err.message}`, {
        printer,
        reconnect: err instanceof DevicePrinterError && err.reconnect,
      });
    }
  }
  const data = bytesToBase64(bytes);
  const local = await localAgentStatus();
  if (local?.agentId === c.agentId && local.printers?.some((p) => p.id === printer.id)) {
    try {
      await printViaLocalAgent(printer.id, data, ref?.label ?? kind);
      return { via: 'local' };
    } catch {
      // fall through to the relay
    }
  }
  // Self-order kiosks print with their device cookie (no staff session).
  const path = context?.printPath ?? '/print/jobs';
  const job = await api.post(path, { printerId: printer.id, kind, ref: ref ?? null, data }).catch((err) => {
    // Offline: the cloud link to the print agent needs internet; local printers still work.
    if (err?.code === 'NETWORK_ERROR')
      throw new PrintError(
        `No internet: ${printer.name} prints through the print agent's online link. Print in the browser, or connect the printer to this device.`,
        { printer },
      );
    throw err;
  });
  if (job.status === 'printed') return { via: 'agent', job };
  if (job.status === 'failed') throw new PrintError(`${printer.name}: ${job.error ?? 'print failed'}`, { printer });
  throw new PrintError(
    job.status === 'queued'
      ? `${printer.agentName ?? 'The print agent'} is offline. It prints when the agent reconnects (within 10 minutes).`
      : `Sent to ${printer.name}; still waiting for the printer.`,
    { printer, pending: true },
  );
}

async function sendDoc(printer, doc, opts) {
  return sendBytes(printer, await renderFor(printer, doc, opts), opts);
}

// ---------------------------------------------------------------- receipts
/**
 * Print a bill's receipt. The server records the print (first print free, reprints audited).
 * auto: called right after a sale — never opens the browser dialog by itself.
 */
export async function printReceipt(
  billId,
  { reprint = true, openDrawer = false, auto = false, model: localModel = null } = {},
) {
  const [printers, cat] = await Promise.all([loadPrinters(), catalog()]);
  const printer = receiptPrinter(printers);
  if (!printer && auto) return { skipped: true };
  // Offline bills print from the model built on the device (the server records it when it syncs).
  const model =
    localModel ?? (await withOverride((headers) => api.post(`/bills/${billId}/print`, { reprint }, { headers })));
  const browser = () => printNode(<ReceiptView model={model} />, { width: model.paperWidth });
  if (!printer) {
    await browser();
    return { via: 'browser', model };
  }
  try {
    const res = await sendDoc(printer, receiptDocument(model, { profile: printer.profile }), {
      kind: 'receipt',
      ref: { billId, label: model.meta?.find((m) => m.label === 'Bill No')?.value ?? 'Receipt' },
      openDrawer,
      logoUrl: cat.business?.logoUrl,
    });
    return { ...res, model, printer };
  } catch (err) {
    if (err instanceof PrintError) err.fallback = browser;
    throw err;
  }
}

/** Kitchen tickets, each routed to its station's printer(s). */
export async function printKots(kots, { auto = false } = {}) {
  if (!kots?.length) return { printed: 0 };
  const [printers, cat] = await Promise.all([loadPrinters(), catalog()]);
  const opts = {
    outletName: cat.outlet?.name,
    timezone: cat.outlet?.timezone,
    large: cat.settings?.kot?.large !== false,
  };
  const unrouted = [];
  const errors = [];
  let printed = 0;
  for (const kot of kots) {
    const targets = kotPrinters(printers, kot.station);
    if (!targets.length) {
      unrouted.push(kot);
      continue;
    }
    for (const p of targets) {
      const k = cat.settings?.kot?.showCreatedBy === false ? { ...kot, createdBy: null } : kot;
      try {
        const res = await sendDoc(p, kotDocument(k, { ...opts, profile: p.profile }), {
          kind: 'kot',
          ref: { kotId: kot.id, label: `KOT ${kot.kotNo}` },
        });
        printed += 1;
        // The relay marks KOTs printed on the server; direct paths report it here.
        if (res.via !== 'agent') await api.post(`/kots/${kot.id}/printed`).catch(() => {});
      } catch (err) {
        errors.push(err);
      }
    }
  }
  const browser = (list) => async () => {
    await printNode(
      <div>
        {list.map((k) => (
          <div key={k.id} style={{ breakAfter: 'page' }}>
            <KotView kot={k} outletName={opts.outletName} paperWidth={cat.settings?.receipt?.paperWidth ?? '80mm'} />
          </div>
        ))}
      </div>,
      { width: cat.settings?.receipt?.paperWidth ?? '80mm' },
    );
    await Promise.all(list.map((k) => api.post(`/kots/${k.id}/printed`).catch(() => {})));
  };
  if (unrouted.length && !auto) await browser(unrouted)();
  if (errors.length) {
    const e = errors.find((x) => !x.pending) ?? errors[0];
    if (e instanceof PrintError) e.fallback = browser(kots);
    throw e;
  }
  return { printed, unrouted: auto ? unrouted.length : 0 };
}

/** Token slip for the customer (counter service). */
export async function printTokenSlip(bill, { auto = false } = {}) {
  const [printers, cat] = await Promise.all([loadPrinters(), catalog()]);
  const printer = tokenPrinter(printers);
  if (!printer && auto) return { skipped: true };
  const slip = {
    businessName: cat.business?.name,
    outletName: cat.outlet?.name,
    tokenNo: bill.tokenNo,
    orderType: bill.orderType,
    itemCount: bill.totals?.itemCount ?? bill.lines?.reduce((s, l) => s + l.qty, 0) ?? 0,
    invoiceNo: bill.invoiceNo,
    date: bill.invoiceDate ?? bill.createdAt,
    timezone: cat.outlet?.timezone,
    footer: cat.settings?.tokenSlip?.footer,
  };
  const browserProfile = profileForPaper(cat.settings?.receipt?.paperWidth ?? '80mm');
  const browser = () =>
    printNode(<ThermalPreview doc={tokenSlipDocument(slip, { profile: browserProfile })} profile={browserProfile} />, {
      width: browserProfile.paperWidth,
    });
  if (!printer) return browser().then(() => ({ via: 'browser' }));
  try {
    return await sendDoc(printer, tokenSlipDocument(slip, { profile: printer.profile }), {
      kind: 'token',
      ref: { billId: bill.id, label: `Token ${bill.tokenNo}` },
    });
  } catch (err) {
    if (err instanceof PrintError) err.fallback = browser;
    throw err;
  }
}

/** Credit note (refund) on the receipt printer. */
export async function printCreditNote(billId, creditNoteId) {
  const [printers, cat, model] = await Promise.all([
    loadPrinters(),
    catalog(),
    api.get(`/bills/${billId}/credit-notes/${creditNoteId}/receipt`),
  ]);
  const printer = receiptPrinter(printers);
  const browser = () => printNode(<ReceiptView model={model} />, { width: model.paperWidth });
  if (!printer) return browser().then(() => ({ via: 'browser' }));
  try {
    return await sendDoc(printer, receiptDocument(model, { profile: printer.profile }), {
      kind: 'credit_note',
      ref: { billId, creditNoteId, label: model.meta?.[0]?.value ?? 'Credit note' },
      logoUrl: cat.business?.logoUrl,
    });
  } catch (err) {
    if (err instanceof PrintError) err.fallback = browser;
    throw err;
  }
}

export async function testPrint(printer) {
  const doc = testPageDocument({
    printerName: printer.name,
    connectionLabel: connectionLabel(printer),
    profile: printer.profile,
    timezone: useSession.getState().me?.outlet?.timezone ?? 'Asia/Kolkata',
  });
  return sendDoc(printer, doc, { kind: 'test', ref: { label: 'Test page' } });
}

/** Manual "Open drawer": permission or manager approval, audited by the server. */
export async function openDrawer(printer) {
  const res = await withOverride((headers) => api.post('/print/drawer', { printerId: printer.id }, { headers }));
  if (res.via === 'device') {
    const bytes = new EscPosEncoder({ codePage: printer.profile.codePage }).initialize().drawer(res.pin).bytes();
    await sendBytes(printer, bytes, { kind: 'drawer' });
    return { via: 'device' };
  }
  if (res.job?.status === 'printed') return { via: 'agent' };
  throw new PrintError(
    res.job?.status === 'failed'
      ? `${printer.name}: ${res.job.error}`
      : `${printer.agentName ?? 'The print agent'} is offline.`,
    { printer, pending: res.job?.status === 'queued' },
  );
}

/**
 * X / shift / Z reports on the receipt printer (browser print dialog if there is none).
 * build(profile) → print document (shiftReportDocument / dayReportDocument).
 */
export async function printReport(build, { label = 'Report' } = {}) {
  const [printers, cat] = await Promise.all([loadPrinters(), catalog()]);
  const printer = receiptPrinter(printers);
  const browserProfile = profileForPaper(cat.settings?.receipt?.paperWidth ?? '80mm');
  const browser = () =>
    printNode(<ThermalPreview doc={build(browserProfile)} profile={browserProfile} />, {
      width: browserProfile.paperWidth,
    });
  if (!printer) return browser().then(() => ({ via: 'browser' }));
  try {
    return await sendDoc(printer, build(printer.profile), { kind: 'report', ref: { label } });
  } catch (err) {
    if (err instanceof PrintError) err.fallback = browser;
    throw err;
  }
}
